import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { brandInfo, credentials, milestones, contactInfo, faqs } from '@/mocks/about';

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'intro' | 'credentials' | 'contact'>('intro');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: brandInfo.name,
    url: 'https://www.kaiyunsports.com',
    description: brandInfo.description,
    email: contactInfo.email,
    telephone: contactInfo.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '金融街8号国际金融中心',
      addressLocality: '中环',
      addressCountry: 'HK',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: contactInfo.phone,
      contactType: 'customer service',
      availableLanguage: ['Chinese', 'English'],
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <Navbar />
      <main className="pt-20 pb-14 min-h-screen bg-background-50">
        <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

        <div className="w-full px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                关于开云体育
              </h1>
              <p className="text-sm text-foreground-500">
                {brandInfo.slogan}
              </p>
            </div>

            <div className="flex items-center justify-center gap-2 mb-10">
              <button
                onClick={() => setActiveTab('intro')}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === 'intro'
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/60'
                }`}
              >
                品牌介绍
              </button>
              <button
                onClick={() => setActiveTab('credentials')}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === 'credentials'
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/60'
                }`}
              >
                资质信誉
              </button>
              <button
                onClick={() => setActiveTab('contact')}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === 'contact'
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/60'
                }`}
              >
                联系我们
              </button>
            </div>

            {activeTab === 'intro' && (
              <div className="space-y-10">
                <div className="bg-background-100 border border-background-200/60 rounded-lg p-6 md:p-8">
                  <h2 className="text-lg font-semibold text-foreground-800 mb-4">品牌简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{brandInfo.description}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <div className="bg-background-50 rounded-lg p-5 border border-background-200/50">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 mb-3">
                        <i className="ri-focus-2-line text-xl"></i>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-800 mb-1">我们的使命</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{brandInfo.mission}</p>
                    </div>
                    <div className="bg-background-50 rounded-lg p-5 border border-background-200/50">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                        <i className="ri-eye-line text-xl"></i>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-800 mb-1">我们的愿景</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{brandInfo.vision}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="text-lg font-semibold text-foreground-800 mb-6 text-center">发展历程</h2>
                  <div className="relative">
                    <div className="absolute left-1/2 -translate-x-[0.5px] top-0 bottom-0 w-px bg-background-300 hidden md:block"></div>
                    <div className="space-y-6">
                      {milestones.map((milestone, index) => (
                        <div
                          key={milestone.year}
                          className={`flex flex-col md:flex-row gap-4 md:gap-8 ${
                            index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                          }`}
                        >
                          <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                            <div className="bg-background-100 border border-background-200/60 rounded-lg p-5 hover:border-primary-300 transition-colors">
                              <span className="text-xs font-bold text-primary-500 bg-primary-50 px-2 py-0.5 rounded">
                                {milestone.year}
                              </span>
                              <h3 className="text-base font-semibold text-foreground-800 mt-2 mb-1">{milestone.title}</h3>
                              <p className="text-sm text-foreground-500 leading-relaxed">{milestone.description}</p>
                            </div>
                          </div>
                          <div className="hidden md:flex items-center justify-center w-10 flex-shrink-0">
                            <div className="w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50"></div>
                          </div>
                          <div className="flex-1 hidden md:block"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'credentials' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {credentials.map((item) => (
                    <div
                      key={item.id}
                      className="bg-background-100 border border-background-200/60 rounded-lg p-6 hover:border-primary-300 transition-all duration-200"
                    >
                      <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                        <i className={`${item.icon} text-2xl`}></i>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-800 mb-2">{item.title}</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{item.description}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-accent-50 border border-accent-200/60 rounded-lg p-5 mt-6">
                  <div className="flex items-center gap-3 mb-2">
                    <i className="ri-shield-star-line text-accent-600 text-xl"></i>
                    <span className="text-sm font-semibold text-accent-800">国际认证与合作伙伴</span>
                  </div>
                  <p className="text-sm text-accent-700 leading-relaxed">
                    开云体育与国际多家权威机构保持紧密合作，所有运营活动均受到严格监管。平台定期接受第三方独立审计，确保每一笔交易、每一次竞猜结算都经得起最严格的检验。
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'contact' && (
              <div className="space-y-8">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-background-100 border border-background-200/60 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-3">
                      <i className="ri-mail-line text-2xl"></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-500">{contactInfo.email}</p>
                  </div>
                  <div className="bg-background-100 border border-background-200/60 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-3">
                      <i className="ri-phone-line text-2xl"></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">客服热线</h3>
                    <p className="text-sm text-foreground-500">{contactInfo.phone}</p>
                  </div>
                  <div className="bg-background-100 border border-background-200/60 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-3">
                      <i className="ri-time-line text-2xl"></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1">服务时间</h3>
                    <p className="text-sm text-foreground-500">{contactInfo.workingHours}</p>
                  </div>
                </div>

                <div className="bg-background-100 border border-background-200/60 rounded-lg p-6 md:p-8">
                  <h2 className="text-lg font-semibold text-foreground-800 mb-4">常见问题</h2>
                  <div className="space-y-3">
                    {faqs.map((faq, index) => (
                      <details key={index} className="group border border-background-200/60 rounded-lg overflow-hidden">
                        <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-50 transition-colors">
                          <span className="text-sm font-medium text-foreground-700 pr-4">{faq.question}</span>
                          <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                        </summary>
                        <div className="px-5 pb-4">
                          <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                        </div>
                      </details>
                    ))}
                  </div>
                </div>

                <div className="bg-background-100 border border-background-200/60 rounded-lg p-6 md:p-8">
                  <h2 className="text-lg font-semibold text-foreground-800 mb-2">公司地址</h2>
                  <p className="text-sm text-foreground-500 mb-4">{contactInfo.address}</p>
                  <div className="rounded-lg overflow-hidden h-[300px]">
                    <iframe
                      title="公司地址"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3691.6796598733494!2d114.15537031542918!3d22.284789585331896!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3404007c9a2a9eb1%3A0x1f5a3a7c6d2b2c7e!2sIFC%20Mall!5e0!3m2!1szh-CN!2shk!4v1690000000000!5m2!1szh-CN!2shk"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}